import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Spinner } from "theme-ui";
import moment from "moment-timezone";

import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import SelectInput from "components/forms/SelectInput";
import { mnt, mntType, mntCard, mntPill } from "theme/maintenanceTokens";
import type { MntStatus } from "theme/maintenanceTokens";

import { getPlanEnrichedSitesPage } from "api/ServicePlans";
import { getMapsAtLocation } from "api/Locations";

import PlanSectionHeader from "./PlanSectionHeader";

/**
 * Plan Detail location roster, built for scale. Spec:
 * `docs/maintenance-module/mockups/26-plan-detail-at-scale.svg` (P-a in
 * WHATS_LEFT.md).
 *
 * WHY THIS REPLACES `EnrichedSitesList` FOR PLAN DETAIL: that component
 * receives the ENTIRE roster as a prop and filters it in the browser.
 * That is correct at 7 rows and unusable at 1,084 (the EMCOR ceiling) —
 * every filter, sort and batch chip was a client-side pass over a full
 * dump. This component owns its own query state and reads
 * `GET /maintenance/plans/:id/sites/page`, which applies search,
 * filters, sort and paging in SQL and returns
 * `{ items, total, page, pageSize, counts }`.
 *
 * SAVED VIEWS are the primary navigation, not the filters. At 1,084 rows
 * the useful question is never "show me everything" — it is "what is
 * overdue", "what has work in flight", "what is in Batch 1". The chips
 * carry live counts from `counts`, which the backend computes against
 * the whole PLAN rather than the active filter, so a chip tells you how
 * many rows you would get if you clicked it.
 *
 * SELECTION IS PAGE-SCOPED, deliberately. "Select all 1,084 and act"
 * needs a server-side bulk contract (act-on-query, not act-on-ids) that
 * does not exist; offering a checkbox that silently only covers the
 * visible 50 would be worse than not offering it. The header checkbox
 * selects the page and says so.
 */

export type PlanRosterRow = {
  lcpId: string;
  lcpName: string | null;
  locationId: string | null;
  address: any;
  city: string | null;
  region: string | null;
  lastServicedDate: string | null;
  groupId: string | null;
  groupName: string | null;
  rosterStatus: string | null;
  batchCount: number;
  status: "wo_received" | "in_progress" | "serviced" | "dormant";
  cadenceStatus: "current" | "due_soon" | "overdue" | null;
  nextDueDate: string | null;
  openWoCount: number;
  addedAt: string;
};

type ViewKey = "all" | "overdue" | "has_open_wo" | "due_soon" | "unbatched" | string;

const PAGE_SIZE = 10;

export default function PlanLocationsTable({
  planId,
  groups,
  intakeStyle,
  canAllocate,
  refreshKey,
  scopeGroupId,
  onCounts,
  woByLcp,
  onMatchWo,
  onOpenWo,
  onAllocate,
  onNewWorkOrder,
  onNewQuote,
  onRemoveFromBatch,
  onAddLocation,
  view: controlledView,
  onViewChange,
}: {
  planId: string;
  groups: Array<{ id: string; name: string }>;
  intakeStyle?: "work_order_fed" | "quote_fed";
  canAllocate: boolean;
  /** Bumped by the parent after any change that could move a row
   * (roster edit, WO raised, plan reload) so the page refetches. */
  refreshKey?: number;
  /** P-i/P-l — the page's shared batch scope. When set, this table shows
   * only that batch and its own batch chips step aside. */
  scopeGroupId?: string | null;
  /** P-m — report the plan-wide saved-view counts up so the page's
   * triage line can state them without a second query. */
  onCounts?: (counts: any) => void;
  /** P-k — the live work order per location, keyed by lcpId, joined in
   * by the parent from the plan's already-loaded WO list. A bare count
   * is useless at volume: what an operator needs from a roster row is
   * what stage the work is at, how long it has sat, and the next action.
   * Absent for locations with nothing raised. */
  woByLcp?: Record<string, any>;
  onMatchWo?: (wo: any) => void;
  onOpenWo?: (woId: string) => void;
  onAllocate?: (lcpIds: string[], groupId: string) => Promise<boolean>;
  onNewWorkOrder?: (lcpId: string) => void;
  onNewQuote?: (lcpId: string) => void;
  onRemoveFromBatch?: (lcpId: string, groupId: string) => Promise<boolean>;
  /** Mockup 26 puts "+ Add locations" in THIS section's header rather
   * than the page header: it acts on the roster, so it belongs next to
   * the roster (and it keeps the page header at two controls, P-f). */
  onAddLocation?: () => void;
  /** P-m — optional controlled saved-view, so the plan's triage line can
   * scope this roster (e.g. "overdue, no WO raised") by setting the
   * view from outside. Falls back to internal state when omitted. */
  view?: string;
  onViewChange?: (v: string) => void;
}) {
  const navigate = useNavigate();

  const [rows, setRows] = useState<PlanRosterRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<any>({});
  const [loading, setLoading] = useState(true);
  // Set when the roster read itself failed (most likely: this API has
  // not been deployed with the paged endpoint yet). Kept separate from
  // an empty result so the table never reports "no locations" for what
  // is actually a transport failure.
  const [loadError, setLoadError] = useState<number | null | "error">(null);

  const [internalView, setInternalView] = useState<ViewKey>("all");
  const view: ViewKey = controlledView !== undefined ? (controlledView as ViewKey) : internalView;
  const setView = (v: ViewKey) => {
    if (onViewChange) onViewChange(v);
    else setInternalView(v);
  };
  const [page, setPage] = useState(1);
  // Collapse the whole roster. At EMCOR scale (125+ sites) the table runs
  // the page many screens long; collapsing folds it to just the header so
  // the sections below (plan config, recent activity) are reachable
  // without a marathon scroll. Default expanded.
  const [collapsed, setCollapsed] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("next_due");
  const [sortDir, setSortDir] = useState<"ASC" | "DESC">("ASC");

  const [selected, setSelected] = useState<string[]>([]);
  const [allocateGroupId, setAllocateGroupId] = useState<string>("");
  const [allocating, setAllocating] = useState(false);

  /**
   * THE OPEN RECORD, and the two densities that hang off it.
   *
   * Null: the list has the full width and its rows earn extra columns —
   * city, batch, state. Set: the list collapses to a 320px column, its
   * rows shed those columns back to name, dot and age, and the record
   * opens beside it.
   *
   * ONE row component, two densities, because the earlier attempts each
   * picked a single density and made the other state either empty or
   * crammed. The list does not need to carry the record once the record
   * is on screen, and it has room to carry more when it is not.
   */
  const [openLcpId, setOpenLcpId] = useState<string | null>(null);
  const openRow = rows.find((r) => r.lcpId === openLcpId) ?? null;

  // Maps for the open site only. Not part of the roster payload, and
  // fetching 152 of them to fill a column nobody is looking at would be
  // the opposite of the point.
  const [openMaps, setOpenMaps] = useState<any[]>([]);
  useEffect(() => {
    if (!openRow?.locationId) {
      setOpenMaps([]);
      return;
    }
    let alive = true;
    getMapsAtLocation(openRow.locationId, false)
      .then((m: any) => {
        if (alive) setOpenMaps(Array.isArray(m) ? m : []);
      })
      .catch(() => {
        if (alive) setOpenMaps([]);
      });
    return () => {
      alive = false;
    };
  }, [openRow?.locationId]);

  // A record that is no longer on the page cannot stay open behind a
  // filter change — the pane would describe a row the list no longer
  // shows.
  useEffect(() => {
    if (openLcpId && !rows.some((r) => r.lcpId === openLcpId)) setOpenLcpId(null);
  }, [rows, openLcpId]);

  const isWorkOrderFed = intakeStyle === "work_order_fed";

  // Debounce the search box: at 1,084 rows every keystroke is a query.
  const searchTimer = useRef<any>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  // A view is a preset over the same query params, not a separate mode.
  const viewParams = useMemo(() => {
    // The page scope wins: when a batch is selected, every view is
    // within that batch.
    const scope = scopeGroupId ? { groupId: scopeGroupId } : {};
    switch (view) {
      case "overdue":
        return { ...scope, cadence: "overdue" };
      case "due_soon":
        return { ...scope, cadence: "due_soon" };
      case "has_open_wo":
        return { ...scope, hasOpenWo: true };
      case "overdue_no_wo":
        // The actionable slice: due for service and nothing raised.
        return { ...scope, cadence: "overdue", hasOpenWo: false };
      case "unbatched":
        return { ...scope, unbatched: true };
      case "all":
        return scope;
      default:
        // Any other value is a batch id (the per-batch chips). An
        // explicit chip choice overrides the page scope.
        return { groupId: view };
    }
  }, [view, scopeGroupId]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getPlanEnrichedSitesPage({
      data: {
        id: planId,
        page,
        pageSize: PAGE_SIZE,
        search: search || null,
        status: status || null,
        sort,
        sortDir,
        ...viewParams,
      },
    });
    setRows(Array.isArray(res?.items) ? res.items : []);
    setTotal(Number(res?.total ?? 0));
    setCounts(res?.counts ?? {});
    if (onCounts) onCounts(res?.counts ?? {});
    setLoadError(res?.error ? res?.status ?? "error" : null);
    setLoading(false);
  }, [planId, page, search, status, sort, sortDir, viewParams, onCounts]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Selection never survives a page or filter change: the rows it
  // referred to are no longer on screen, and acting on invisible
  // selections is how people delete the wrong thing.
  useEffect(() => {
    setSelected([]);
  }, [page, view, search, status, sort, sortDir]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allOnPageSelected = rows.length > 0 && selected.length === rows.length;

  const toggleRow = (lcpId: string) => {
    setSelected((prev) =>
      prev.includes(lcpId) ? prev.filter((x) => x !== lcpId) : [...prev, lcpId],
    );
  };
  const toggleAllOnPage = () => {
    setSelected(allOnPageSelected ? [] : rows.map((r) => r.lcpId));
  };

  const runAllocate = async () => {
    if (!onAllocate || !allocateGroupId || selected.length === 0) return;
    setAllocating(true);
    const ok = await onAllocate(selected, allocateGroupId);
    setAllocating(false);
    if (ok) {
      setSelected([]);
      setAllocateGroupId("");
      load();
    }
  };

  const setSortColumn = (col: string) => {
    if (sort === col) {
      setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    } else {
      setSort(col);
      // Dates and names read ascending first; counts read descending.
      setSortDir(col === "open_wo" ? "DESC" : "ASC");
    }
    setPage(1);
  };

  const chips: Array<{ key: ViewKey; label: string; tone?: "overdue" | "due" }> = [
    { key: "all", label: "All " + (counts.all ?? total) },
    { key: "overdue", label: "Overdue · " + (counts.overdue ?? 0), tone: "overdue" },
    {
      key: "overdue_no_wo",
      label: "Overdue, no WO · " + (counts.overdueNoWo ?? 0),
      tone: "overdue",
    },
    { key: "has_open_wo", label: "Has open WO · " + (counts.hasOpenWo ?? 0) },
    { key: "due_soon", label: "Due soon · " + (counts.dueSoon ?? 0), tone: "due" },
    ...groups.map((g) => ({ key: g.id as ViewKey, label: g.name })),
    { key: "unbatched", label: "Unbatched · " + (counts.unbatched ?? 0) },
  ];

  return (
    <Box sx={{ marginBottom: "24px" }}>
      <PlanSectionHeader
        label="Locations"
        hint={
          loading
            ? "loading"
            : `${total.toLocaleString()} ${scopeGroupId ? "in this batch" : "in this plan"}`
        }
        action={
          <Flex sx={{ alignItems: "baseline", gap: "14px" }}>
            {onAddLocation && !collapsed ? (
              <Box
                as="button"
                onClick={onAddLocation}
                sx={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontFamily: "Poppins",
                  fontSize: "11px",
                  color: mnt.link,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                + Add locations
              </Box>
            ) : null}
            <Box
              as="button"
              onClick={() => setCollapsed((c) => !c)}
              sx={{
                background: "transparent",
                border: "none",
                padding: 0,
                fontFamily: "Poppins",
                fontSize: "11px",
                color: mnt.link,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {collapsed ? "Show" : "Hide"}
            </Box>
          </Flex>
        }
      />

      {!collapsed && (
        <>
      {/* Saved views. Primary navigation at this scale. */}
      <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
        {/* Batch chips are hidden while the page is scoped to a batch:
         * the scope bar above already says which one, and offering a
         * second, conflicting batch control on the same page is how you
         * get two sources of truth. */}
        {(scopeGroupId ? chips.filter((c) => !groups.some((g) => g.id === c.key)) : chips).map(
          (c) => {
          const active = view === c.key;
          return (
            <Box
              key={String(c.key)}
              as="button"
              onClick={() => {
                setView(c.key);
                setPage(1);
              }}
              sx={{
                background: active ? mnt.ink : mnt.cardBg,
                border: "0.5px solid",
                borderColor: active ? mnt.ink : mnt.divider,
                borderRadius: "12px",
                padding: "5px 12px",
                fontFamily: "Poppins",
                fontSize: "11px",
                color: active
                  ? mnt.cardBg
                  : c.tone === "overdue"
                  ? mnt.red
                  : c.tone === "due"
                  ? mnt.amber
                  : mnt.ink,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {c.label}
            </Box>
          );
        })}
      </Flex>

      {/* Filter row */}
      <Flex sx={{ gap: "10px", flexWrap: "wrap", marginBottom: "12px", alignItems: "center" }}>
        <Box sx={{ flex: "1 1 240px", maxWidth: "300px" }}>
          <input
            type="text"
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search location, city, address"
            style={{
              width: "100%",
              padding: "7px 10px",
              fontFamily: "Poppins",
              fontSize: "12px",
              color: mnt.ink,
              border: "0.5px solid " + mnt.divider,
              borderRadius: "4px",
              outline: "none",
            }}
          />
        </Box>
        <Box sx={{ minWidth: "170px" }}>
          <SelectInput
            name="rosterStatus"
            value={status}
            options={[
              { value: "", label: "Any activity" },
              { value: "wo_received", label: "Work order open" },
              { value: "in_progress", label: "In production" },
              { value: "serviced", label: "Serviced" },
              { value: "dormant", label: "No history" },
            ]}
            onChange={(v: any) => {
              setStatus(v ?? "");
              setPage(1);
            }}
            searchable={false}
            clearable={false}
          />
        </Box>
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
          Sorted by {sortLabel(sort)} {sortDir === "ASC" ? "ascending" : "descending"}
        </Subtext>
      </Flex>

      {/* Bulk action bar — at the TOP of the roster when rows are
          selected, so the batch action is visible without scrolling past
          the table. Page-scoped selection (see the component header). */}
      {canAllocate && selected.length > 0 && (
        <Flex
          sx={{
            background: mnt.ink,
            borderRadius: "8px",
            padding: "10px 16px",
            marginBottom: "10px",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.onInk }}>
            {selected.length} selected on this page
          </Box>
          <Box sx={{ minWidth: "180px" }}>
            <select
              value={allocateGroupId}
              onChange={(e: any) => setAllocateGroupId(e.target.value)}
              style={{
                width: "100%",
                padding: "5px 8px",
                fontFamily: "Poppins",
                fontSize: "11px",
                borderRadius: "4px",
                border: "none",
              }}
            >
              <option value="">Choose a batch...</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </Box>
          <Box
            as="button"
            onClick={runAllocate}
            sx={{
              background: allocateGroupId ? mnt.cardBg : "transparent",
              border: "1px solid",
              borderColor: allocateGroupId ? mnt.cardBg : mnt.textMuted,
              borderRadius: "4px",
              padding: "5px 12px",
              fontFamily: "Poppins",
              fontSize: "11px",
              color: allocateGroupId ? mnt.ink : mnt.textFaint,
              cursor: allocateGroupId ? "pointer" : "default",
              opacity: allocating ? 0.6 : 1,
            }}
          >
            Add to batch
          </Box>
          <Box
            as="button"
            onClick={() => setSelected([])}
            sx={{
              marginLeft: "auto",
              background: "transparent",
              border: "none",
              fontFamily: "Poppins",
              fontSize: "11px",
              color: mnt.onInkMuted,
              cursor: "pointer",
            }}
          >
            Clear selection
          </Box>
        </Flex>
      )}

      <Flex sx={{ gap: "16px", alignItems: "flex-start" }}>
      <Box
        sx={{
          ...mntCard,
          overflow: "hidden",
          // The one line that drives both densities.
          flex: openRow ? "0 0 320px" : "1 1 auto",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Flex
          sx={{
            padding: "10px 16px",
            background: mnt.bgSubtle,
            borderBottom: "0.5px solid",
            borderColor: mnt.divider,
            gap: "10px",
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: "0 0 20px" }}>
            {canAllocate && (
              <input
                type="checkbox"
                checked={allOnPageSelected}
                onChange={toggleAllOnPage}
                title="Select the rows on this page"
              />
            )}
          </Box>

          {/* COLUMN HEADERS ARE GONE, AND THE SORT MOVED WITH THEM.

              The rows are no longer six aligned columns, so six headers
              would label nothing. Click-to-sort lived on those headers,
              which is why it is a select here instead — dropping the
              headers without replacing the sort would have quietly taken
              a working feature away, and sorting a 152-site roster is
              the whole reason someone opens this tab.

              The current sort is stated in words beside it rather than
              inferred from a caret: nothing on screen carries the arrow
              any more. */}
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px" }}>
            SORT
          </Box>
          <select
            value={sort}
            onChange={(e) => setSortColumn(e.target.value as any)}
            style={{
              fontFamily: "Poppins",
              fontSize: "12px",
              padding: "4px 8px",
              borderRadius: "6px",
              border: `1px solid ${mnt.divider}`,
              background: mnt.cardBg,
              color: mnt.ink,
              cursor: "pointer",
            }}
          >
            <option value="next_due">Next due</option>
            <option value="name">Location</option>
            <option value="last_service">Last serviced</option>
            <option value="open_wo">Work order</option>
            <option value="batch">Batch</option>
          </select>
          {/* A BUTTON, not a label: clicking a header a second time is
              how direction used to flip, and a select only fires when
              the VALUE changes — so without this, picking the sort you
              are already on does nothing and the order can never be
              reversed. Removing the headers quietly removed that.

              Plain <button>, not `Box as="button"`: theme-ui's BoxProps
              has no `type`, and omitting `type` on a button inside a
              form makes it a submit. Same fix as QueueDrillDown. */}
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"))}
            title="Reverse the order"
            style={{
              fontFamily: "Poppins",
              fontSize: "11px",
              color: mnt.link,
              whiteSpace: "nowrap",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            {sortDir === "ASC" ? "ascending" : "descending"}
          </button>
        </Flex>

        {loading ? (
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        ) : loadError ? (
          <Box sx={{ padding: "30px", textAlign: "center" }}>
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.amber}>
              {loadError === 404
                ? "This API does not have the paged roster endpoint yet. It ships with the next backend deploy; the rest of the page is unaffected."
                : "Could not load the locations for this plan. Try again in a moment."}
            </Subtext>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ padding: "36px", textAlign: "center" }}>
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
              {search || status || view !== "all"
                ? "No locations match this view."
                : "No locations on this plan yet."}
            </Subtext>
          </Box>
        ) : (
          rows.map((r, idx) => (
            <RosterRow
              key={r.lcpId}
              row={r}
              isLast={idx === rows.length - 1}
              selectable={canAllocate}
              selected={selected.includes(r.lcpId)}
              onToggle={() => toggleRow(r.lcpId)}
              dense={!!openRow}
              isOpen={r.lcpId === openLcpId}
              onOpen={() => setOpenLcpId((cur) => (cur === r.lcpId ? null : r.lcpId))}
              onOpenLocation={
                r.locationId ? () => navigate("/locations/" + r.locationId) : undefined
              }
              onRaise={
                isWorkOrderFed
                  ? onNewWorkOrder
                    ? () => onNewWorkOrder(r.lcpId)
                    : undefined
                  : onNewQuote
                  ? () => onNewQuote(r.lcpId)
                  : undefined
              }
              raiseLabel={isWorkOrderFed ? "+ WO" : "+ Quote"}
              wo={woByLcp?.[r.lcpId]}
              onMatchWo={onMatchWo}
              onOpenWo={onOpenWo}
              onRemoveFromBatch={
                onRemoveFromBatch && r.groupId
                  ? async () => {
                      const gid = r.groupId as string;
                      const ok = await onRemoveFromBatch(r.lcpId, gid);
                      if (ok) load();
                    }
                  : undefined
              }
            />
          ))
        )}
      </Box>

      {openRow && (
        <SiteDetail
          row={openRow}
          maps={openMaps}
          wo={woByLcp?.[openRow.lcpId]}
          isWorkOrderFed={isWorkOrderFed}
          onClose={() => setOpenLcpId(null)}
          onOpenLocation={
            openRow.locationId
              ? () => navigate("/locations/" + openRow.locationId)
              : undefined
          }
          onOpenWo={onOpenWo}
          onMatchWo={onMatchWo}
          onRaise={
            isWorkOrderFed
              ? onNewWorkOrder
                ? () => onNewWorkOrder(openRow.lcpId)
                : undefined
              : onNewQuote
              ? () => onNewQuote(openRow.lcpId)
              : undefined
          }
        />
      )}
      </Flex>

      {/* Pagination */}
      <Flex sx={{ marginTop: "10px", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
          {total === 0
            ? "No rows"
            : "Showing " +
              ((page - 1) * PAGE_SIZE + 1).toLocaleString() +
              " to " +
              Math.min(page * PAGE_SIZE, total).toLocaleString() +
              " of " +
              total.toLocaleString()}
        </Subtext>
        {pageCount > 1 && (
          <Flex sx={{ alignItems: "center", gap: "6px" }}>
            <PageButton label="‹" disabled={page <= 1} onClick={() => setPage(page - 1)} />
            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
              Page {page} of {pageCount}
            </Subtext>
            <PageButton
              label="›"
              disabled={page >= pageCount}
              onClick={() => setPage(page + 1)}
            />
          </Flex>
        )}
      </Flex>
        </>
      )}
    </Box>
  );
}

/* ─── pieces ──────────────────────────────────────────────────────── */

function RosterRow({
  row,
  isLast,
  selectable,
  selected,
  onToggle,
  dense,
  isOpen,
  onOpen,
  onOpenLocation,
  onRaise,
  raiseLabel,
  wo,
  onMatchWo,
  onOpenWo,
  onRemoveFromBatch,
}: {
  row: PlanRosterRow;
  isLast: boolean;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
  /** True while a record is open: the row sheds its extra columns. */
  dense: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onOpenLocation?: () => void;
  onRaise?: () => void;
  raiseLabel: string;
  wo?: any;
  onMatchWo?: (wo: any) => void;
  onOpenWo?: (woId: string) => void;
  onRemoveFromBatch?: () => void;
}) {
  const pill = statusPill(row);
  const name = row.lcpName || row.address?.line1 || "Unnamed location";
  const age = wo?.receivedAt ? moment().diff(moment(wo.receivedAt), "days") + "d" : "";

  /**
   * THE DOT, not a pill, in the row.
   *
   * At twelve rows a column of coloured pills is a wall of identical
   * shapes and the eye stops reading any of them. The dot carries the
   * same four states at a glance; the WORD is in the pane, where you are
   * already looking once you have chosen a row. In the wide state the
   * word also gets its own column, because there is room for it there.
   */
  const dotColor = pill.muted
    ? mnt.textFaint
    : pill.status === "overdue"
    ? mnt.red
    : pill.status === "due" || pill.status === "woReceived"
    ? mnt.amber
    : pill.status === "scheduled"
    ? mnt.link
    : mnt.green;

  return (
    <Flex
      sx={{
        alignItems: "center",
        gap: dense ? "10px" : "12px",
        padding: dense ? "10px 14px" : "10px 16px",
        borderBottom: isLast ? "none" : "0.5px solid",
        borderColor: mnt.divider,
        background: isOpen ? mnt.bgBeige : selected ? mnt.bgBeige : "transparent",
        cursor: "pointer",
        "&:hover": { background: isOpen ? mnt.bgBeige : mnt.bgSubtle },
      }}
      onClick={onOpen}
    >
      {/* The checkbox is a WIDE-state control. Batch selection wants the
          whole table; once the pane is open the row is a navigation
          target and a checkbox beside it competes with the click that
          opens it. */}
      {!dense && (
        <Box sx={{ flex: "0 0 20px" }} onClick={(e: any) => e.stopPropagation()}>
          {selectable && <input type="checkbox" checked={selected} onChange={onToggle} />}
        </Box>
      )}

      <Box
        sx={{
          width: "7px",
          height: "7px",
          borderRadius: "4px",
          background: dotColor,
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
        <Box
          as="div"
          sx={{
            fontFamily: "Poppins",
            fontSize: "12.5px",
            fontWeight: 500,
            color: mnt.link,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </Box>
        {/* The city rides under the name ONLY when the row is narrow.
            Wide, it has its own column and repeating it here would be
            the cram this design removed. */}
        {dense && row.city && (
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "10.5px",
              color: mnt.textFaint,
              marginTop: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.city}
          </Box>
        )}
      </Box>

      {/* WIDE ONLY: the columns the width pays for. */}
      {!dense && (
        <>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "11.5px",
              color: mnt.textMuted,
              width: "150px",
              flexShrink: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.city || ""}
          </Box>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "11.5px",
              color: mnt.textMuted,
              width: "96px",
              flexShrink: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.groupName || ""}
          </Box>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "11.5px",
              color: pill.muted ? mnt.textFaint : mnt.textMuted,
              width: "118px",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {pill.muted ? "No work raised" : pill.label}
          </Box>
        </>
      )}

      <Box
        as="div"
        sx={{
          fontFamily: "Poppins",
          fontSize: "11px",
          color: mnt.textMuted,
          width: dense ? "34px" : "40px",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {age}
      </Box>
    </Flex>
  );
}

/**
 * THE OPEN SITE, beside the list.
 *
 * This is where everything the row stopped carrying went. The row's job
 * is to help you FIND a site; this answers what is true about it and
 * what you can do, with room to say both properly instead of
 * compressing them into columns.
 *
 * Measured yardage comes from the same `getMapsAtLocation` read the
 * operator case file uses, for the open site only.
 */
function SiteDetail({
  row,
  maps,
  wo,
  isWorkOrderFed,
  onClose,
  onOpenLocation,
  onOpenWo,
  onMatchWo,
  onRaise,
}: {
  row: PlanRosterRow;
  maps: any[];
  wo?: any;
  isWorkOrderFed: boolean;
  onClose: () => void;
  onOpenLocation?: () => void;
  onOpenWo?: (woId: string) => void;
  onMatchWo?: (wo: any) => void;
  onRaise?: () => void;
}) {
  const pill = statusPill(row);
  const name = row.lcpName || row.address?.line1 || "Unnamed location";
  const addr = row.address || {};
  const line = [addr.line1, addr.city || row.city, addr.state, addr.postalCode]
    .filter(Boolean)
    .join(", ");
  const totalYd = maps.reduce((s, m) => s + Number(m?.totalCuYd || 0), 0);
  const zones = maps.reduce((s, m) => s + Number(m?.zoneCount || 0), 0);
  const stage = wo ? woStagePresentation(wo) : null;
  const age = wo?.receivedAt ? moment().diff(moment(wo.receivedAt), "days") : null;

  const secondary = {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 500,
    padding: "8px 14px",
    borderRadius: "6px",
    background: mnt.cardBg,
    border: `1px solid ${mnt.divider}`,
    color: mnt.ink,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  };

  return (
    <Box sx={{ ...mntCard, flex: "1 1 auto", minWidth: 0, padding: "20px 22px" }}>
      <Flex sx={{ alignItems: "flex-start", gap: "14px", marginBottom: "16px" }}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "17px", fontWeight: 600 }}>
            {name}
          </Box>
          <Box
            as="div"
            sx={{ fontFamily: "Poppins", fontSize: "11.5px", color: mnt.textMuted, marginTop: "4px" }}
          >
            {[line || null, row.groupName || null].filter(Boolean).join(" \u00b7 ")}
          </Box>
        </Box>
        {!pill.muted && (
          <Box sx={{ ...mntPill(pill.status), fontSize: "10px", flexShrink: 0 }}>{pill.label}</Box>
        )}
        <button type="button" onClick={onClose} aria-label="Close this site" style={{ ...secondary, padding: "6px 10px" }}>
          Close
        </button>
      </Flex>

      <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "18px" }}>
        {/* The raise action is the PRIMARY here and nowhere else. In the
            row it was one of six things competing; here it is the thing
            the pane exists to let you do. */}
        {onRaise && !wo && (
          <button
            type="button"
            onClick={onRaise}
            style={{
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: 500,
              padding: "8px 14px",
              borderRadius: "6px",
              background: mnt.ink,
              border: "none",
              color: mnt.cardBg,
              cursor: "pointer",
            }}
          >
            {isWorkOrderFed ? "Create work order" : "Raise quote"}
          </button>
        )}
        {wo && onMatchWo && wo?.matchStatus === "unmatched" && (
          <button type="button" onClick={() => onMatchWo(wo)} style={{ ...secondary, background: mnt.ink, color: mnt.cardBg, border: "none" }}>
            Match to a site
          </button>
        )}
        {wo && onOpenWo && (
          <button type="button" onClick={() => onOpenWo(wo.id)} style={secondary}>
            Open the work order
          </button>
        )}
        {onOpenLocation && (
          <button type="button" onClick={onOpenLocation} style={secondary}>
            Open the site
          </button>
        )}
      </Flex>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "10px",
          marginBottom: "18px",
        }}
      >
        <DetailStat
          label="MEASURED"
          value={maps.length ? totalYd.toFixed(1) + " cu yd" : "Not yet"}
          sub={
            maps.length
              ? zones + (zones === 1 ? " zone" : " zones")
              : "No map at this site"
          }
        />
        <DetailStat
          label="LAST SERVICED"
          value={row.lastServicedDate ? moment(row.lastServicedDate).format("MMM 'YY") : "Never"}
          sub={row.nextDueDate ? "next " + nextDueLabel(row) : "No cadence set"}
        />
        <DetailStat
          label="OPEN WORK"
          value={String(row.openWoCount || 0)}
          sub={age != null ? "raised " + age + " days ago" : "Nothing raised"}
        />
      </Box>

      {wo && stage && (
        <Box>
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px", marginBottom: "7px" }}>
            WORK AT THIS SITE
          </Box>
          <Box sx={{ border: `1px solid ${mnt.divider}`, borderRadius: "8px", padding: "12px 14px" }}>
            <Flex sx={{ alignItems: "center", gap: "10px" }}>
              <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "12.5px", fontWeight: 500 }}>
                {wo.externalId || wo.name || "Work order"}
              </Box>
              <Box sx={{ flex: "1 1 auto" }} />
              <Box sx={{ ...mntPill(stage.tone), fontSize: "10px" }}>{stage.label}</Box>
            </Flex>
            {wo.problemDescription && (
              <Box
                as="div"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "11.5px",
                  color: mnt.textMuted,
                  marginTop: "5px",
                  lineHeight: "17px",
                }}
              >
                {wo.problemDescription}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function DetailStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Box sx={{ background: mnt.bgSubtle, borderRadius: "8px", padding: "13px 15px" }}>
      <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px" }}>{label}</Box>
      <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "19px", fontWeight: 600, marginTop: "5px" }}>
        {value}
      </Box>
      <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "11px", color: mnt.textMuted, marginTop: "2px" }}>
        {sub}
      </Box>
    </Box>
  );
}

/**
 * P-k — the roster's work-order cell. Three shapes:
 *   - nothing raised: the raise action, so an overdue location can be
 *     acted on straight from the roster;
 *   - a work order needing a match: stage, age, and Match inline, which
 *     is the one step that genuinely completes in a modal;
 *   - anything further along: stage, age, and a link into the case file,
 *     because pricing/quoting/posting belong on the work order where the
 *     full context is (RC5 cuts both ways — it is about not scattering
 *     the work, not about cramming every action everywhere).
 */
function WorkOrderCell({
  wo,
  openWoCount,
  onMatchWo,
  onOpenWo,
  onRaise,
  raiseLabel,
}: {
  wo?: any;
  openWoCount: number;
  onMatchWo?: (wo: any) => void;
  onOpenWo?: (woId: string) => void;
  onRaise?: () => void;
  raiseLabel: string;
}) {
  if (!wo) {
    // The roster may know a WO exists even when the plan list did not
    // hand us one (a WO on a different plan, say). Say so rather than
    // offering to raise a duplicate.
    if (openWoCount > 0) {
      return (
        <Span fontFamily={"Poppins"} fontSize={"11px"} color={mnt.textMuted}>
          {openWoCount} open
        </Span>
      );
    }
    if (!onRaise) {
      return (
        <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textFaint}>
          none raised
        </Span>
      );
    }
    return (
      <button
        type="button"
        onClick={onRaise}
        style={{
          background: mnt.ink,
          border: "none",
          borderRadius: "4px",
          padding: "4px 10px",
          fontFamily: "Poppins",
          fontSize: "10.5px",
          color: mnt.cardBg,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {raiseLabel === "+ WO" ? "Create work order" : "Raise quote"}
      </button>
    );
  }

  const stage = woStagePresentation(wo);
  const age = wo?.receivedAt ? moment().diff(moment(wo.receivedAt), "days") : null;

  // A cancelled WO is terminal: it no longer occupies the location. The
  // row should be workable again - lead with the raise action like a
  // fresh site - with a single quiet link to the cancelled WO for
  // context. Without this the location was stuck showing only
  // "Cancelled" with no way to start new work.
  if (wo?.status === "rejected") {
    return (
      <Box sx={{ minWidth: 0 }}>
        {onRaise && (
          <button
            type="button"
            onClick={onRaise}
            style={{
              background: mnt.ink,
              border: "none",
              borderRadius: "4px",
              padding: "4px 10px",
              fontFamily: "Poppins",
              fontSize: "10.5px",
              color: mnt.cardBg,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {raiseLabel === "+ WO" ? "Create work order" : "Raise quote"}
          </button>
        )}
        {onOpenWo && (
          <Box
            as="button"
            onClick={() => onOpenWo(wo.id)}
            sx={{
              display: "block",
              background: "transparent",
              border: "none",
              padding: 0,
              marginTop: "3px",
              fontFamily: "Poppins",
              fontSize: "10px",
              color: mnt.textMuted,
              cursor: "pointer",
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View cancelled WO
          </Box>
        )}
      </Box>
    );
  }

  const needsMatch =
    wo?.matchStatus === "unmatched" &&
    (wo?.status === "received" || wo?.status === "matched");

  return (
    <Flex sx={{ alignItems: "center", gap: "8px", minWidth: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ ...mntPill(stage.tone), fontSize: "9.5px" }}>{stage.label}</Box>
        {age != null && (
          <Box sx={{ ...mntType.labelSm, fontSize: "10px", marginTop: "2px" }}>{age}d old</Box>
        )}
      </Box>
      {needsMatch && onMatchWo ? (
        <button
          type="button"
          onClick={() => onMatchWo(wo)}
          style={{
            background: mnt.cardBg,
            border: "1px solid " + mnt.ink,
            borderRadius: "4px",
            padding: "4px 10px",
            fontFamily: "Poppins",
            fontSize: "10.5px",
            color: mnt.ink,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Match
        </button>
      ) : onOpenWo ? (
        <button
          type="button"
          onClick={() => onOpenWo(wo.id)}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            fontFamily: "Poppins",
            fontSize: "10.5px",
            color: mnt.link,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Open WO
        </button>
      ) : null}
    </Flex>
  );
}

/** Stage label + pill tone for a roster row's work order. Mirrors the
 * shared `lifecycleBadge` vocabulary; kept local because the roster
 * needs maintenance-token tones rather than theme badge variants. */
function woStagePresentation(wo: any): { label: string; tone: MntStatus } {
  const stage = wo?.effectiveStage as string | undefined;
  if (wo?.status === "rejected") return { label: "Cancelled", tone: "closed" };
  switch (stage) {
    case "rfq_generated":
      // A dead quote reads as such, not as never quoted (2026-10-16).
      if (/^Quote expired/i.test(String(wo?.stageHint ?? ""))) return { label: "Quote expired", tone: "due" };
      if (/^Quote superseded/i.test(String(wo?.stageHint ?? ""))) return { label: "Re-quote needed", tone: "due" };
      return { label: "Needs quote", tone: "due" };
    case "quote_sent":
      return { label: "Quoted", tone: "woReceived" };
    case "accepted":
      return { label: "Accepted", tone: "onTrack" };
    case "in_production":
      return { label: "In production", tone: "current" };
    case "invoiced":
      return { label: "Invoiced", tone: "closed" };
    case "declined":
      return { label: "Declined", tone: "overdue" };
    default:
      return wo?.matchStatus === "unmatched"
        ? { label: "Needs match", tone: "overdue" }
        : { label: "Needs RFQ", tone: "due" };
  }
}

function PageButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      onClick={disabled ? undefined : onClick}
      sx={{
        background: mnt.cardBg,
        border: "0.5px solid",
        borderColor: mnt.divider,
        borderRadius: "4px",
        padding: "3px 10px",
        fontFamily: "Poppins",
        fontSize: "12px",
        color: disabled ? mnt.textFaint : mnt.ink,
        cursor: disabled ? "default" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {label}
    </Box>
  );
}

/* ─── row vocabulary ──────────────────────────────────────────────── */

/**
 * The schedule pill leads when the plan has a real cadence, because
 * "is this site due" is the question the roster exists to answer. Plans
 * with no recurring cadence fall back to the operational pill, which is
 * the only honest signal available there.
 */
function statusPill(row: PlanRosterRow): { status: MntStatus; label: string; muted?: boolean } {
  if (row.cadenceStatus === "overdue") return { status: "overdue", label: "Overdue" };
  if (row.cadenceStatus === "due_soon") return { status: "due", label: "Due soon" };
  if (row.cadenceStatus === "current") return { status: "current", label: "Current" };

  switch (row.status) {
    case "wo_received":
      return { status: "woReceived", label: "WO open" };
    case "in_progress":
      return { status: "scheduled", label: "In production" };
    case "serviced":
      return { status: "current", label: "Serviced" };
    default:
      // Dormant: nothing has happened here. The row already says this
      // everywhere (Last "-", Next Due "not scheduled", the Create
      // action), so a loud grey "No history" pill is a fourth redundant
      // restatement that buries the pills that DO matter (Overdue, WO
      // open) in a wall of identical grey. Flag it muted so the row
      // renders a quiet dash instead of a pill.
      return { status: "ungrouped", label: "No history", muted: true };
  }
}

function nextDueLabel(row: PlanRosterRow): string {
  if (row.cadenceStatus === "overdue" && !row.nextDueDate) return "never serviced";
  if (!row.nextDueDate) return "not scheduled";
  const due = moment(row.nextDueDate);
  const days = due.diff(moment(), "days");
  if (days < 0) return Math.abs(days) + "d ago";
  return due.format("MMM D, YYYY");
}

function sortLabel(sort: string): string {
  switch (sort) {
    case "name":
      return "location";
    case "city":
      return "city";
    case "last_service":
      return "last service";
    case "open_wo":
      return "open work orders";
    case "batch":
      return "batch";
    case "added":
      return "date added";
    default:
      return "next due";
  }
}
